// Die Seiten der Anwendung. Zusammen in einer Datei, weil jede für sich kurz
// ist und sie sich dieselben Bausteine teilen — sieben Dateien mit je dreißig
// Zeilen wären mehr Ablage als Ordnung.

import { useState } from "react";
import { api } from "../api.ts";
import { eur, eur2, datum, anzahl } from "../formate.ts";
import { KennzahlKacheln, Kpi, Mini, Leer } from "../komponenten/Bausteine.tsx";
import {
  ObjektBlatt, EinheitBlatt, VertragBlatt, PostenBlatt, MangelBlatt,
  InventarBlatt, VorlageBlatt, VorlageAnwendenBlatt, VorlageAusEinheitBlatt,
} from "../komponenten/Blaetter.tsx";
import { useBestand, useZustand } from "../zustand.tsx";
import { BenutzerVerwaltung } from "./Benutzer.tsx";
import {
  ObjektKarte, EinheitZeile, MaengelListe, InventarTabelle, istKritisch,
} from "./Listen.tsx";
import {
  POSTEN_TEXT, STATUS_TEXT, RAEUME,
  type Einheit, type InventarPosition, type Mangel, type Objekt,
  type Sanierungsposten, type VorlagePosition,
} from "../typen.ts";

/** Alle Einheiten über alle Objekte — in mehreren Listen gebraucht. */
const alleEinheiten = (objekte: Objekt[]) =>
  objekte.flatMap((objekt) => objekt.einheiten.map((einheit) => ({ objekt, einheit })));

const alleMaengel = (objekte: Objekt[]) =>
  objekte.flatMap((objekt) => objekt.maengel.map((mangel) => ({ objekt, mangel })));

/** Raumnamen aus Vorlage und erfasstem Inventar, für die Vorschlagslisten. */
const raumVorschlaege = (objekte: Objekt[], vorlage: VorlagePosition[]) =>
  [...new Set([
    ...RAEUME,
    ...vorlage.map((v) => v.raum),
    ...objekte.flatMap((o) => o.einheiten.flatMap((e) => e.inventar.map((i) => i.raum))),
  ].filter(Boolean))];

// --------------------------------------------------------------- Dashboard

export function Dashboard() {
  const bestand = useBestand();
  const { gehe } = useZustand();
  const [neuesObjekt, setzeNeuesObjekt] = useState(false);

  const offen = alleMaengel(bestand.objekte).filter(({ mangel }) => !mangel.erledigt);
  const hoch = offen.filter(({ mangel }) => mangel.prio === "hoch");
  const u = bestand.uebersicht;
  const einheiten = alleEinheiten(bestand.objekte);
  const nachStatus = {
    sanierung: einheiten.filter(({ einheit }) => einheit.status === "sanierung").length,
    vermietbar: einheiten.filter(({ einheit }) => einheit.status === "vermietbar").length,
    vermietet: einheiten.filter(({ einheit }) => einheit.status === "vermietet").length,
  };

  return (
    <>
      {hoch.length > 0 ? (
        <div className="banner warn">
          <span>{anzahl(hoch.length, "Mangel", "Mängel")} mit Priorität „Hoch“ offen.</span>
          <button style={{ background: "var(--orange-ink)" }}
            onClick={() => gehe({ seite: "maengel" })}>Zur Mängelliste</button>
        </div>
      ) : null}

      {/* Fristen gehören aufs Dashboard. Eine Aufgabenliste, die man erst
          aufrufen muss, um zu sehen, dass etwas überfällig ist, hilft nicht. */}
      {u.aufgabenUeberfaellig > 0 ? (
        <div className="banner warn" style={{ background: "var(--red-tint)", color: "#C4241C" }}>
          <span>
            {anzahl(u.aufgabenUeberfaellig, "Aufgabe ist", "Aufgaben sind")} überfällig.
          </span>
          <button style={{ background: "#C4241C" }}
            onClick={() => gehe({ seite: "aufgaben" })}>Zu den Aufgaben</button>
        </div>
      ) : u.aufgabenDemnaechst > 0 ? (
        <div className="banner">
          <span>
            {anzahl(u.aufgabenDemnaechst, "Aufgabe wird", "Aufgaben werden")} in den
            nächsten sieben Tagen fällig.
          </span>
          <button onClick={() => gehe({ seite: "aufgaben" })}>Zu den Aufgaben</button>
        </div>
      ) : null}

      {u.dokumenteAbgelaufen > 0 || u.dokumenteLaufenAus > 0 ? (
        <div className="banner warn">
          <span>
            {u.dokumenteAbgelaufen > 0
              ? `${anzahl(u.dokumenteAbgelaufen, "Dokument ist", "Dokumente sind")} abgelaufen`
              : `${anzahl(u.dokumenteLaufenAus, "Dokument läuft", "Dokumente laufen")} demnächst aus`}
            {u.dokumenteAbgelaufen > 0 && u.dokumenteLaufenAus > 0
              ? `, ${u.dokumenteLaufenAus} weitere laufen demnächst aus.` : "."}
          </span>
          <button style={{ background: "var(--orange-ink)" }}
            onClick={() => gehe({ seite: "dokumente" })}>Zu den Dokumenten</button>
        </div>
      ) : null}

      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <p className="sub">
            {anzahl(bestand.objekte.length, "Objekt", "Objekte")} · {anzahl(einheiten.length, "Einheit", "Einheiten")}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setzeNeuesObjekt(true)}>+ Objekt anlegen</button>
      </div>

      <KennzahlKacheln k={bestand.kennzahlen} />

      {bestand.objekte.length === 0 ? (
        <Leer titel="Noch keine Objekte angelegt.">
          Lege das erste Objekt mit Kaufpreis und Nebenkosten an.
          <br /><br />
          <button className="btn btn-primary" onClick={() => setzeNeuesObjekt(true)}>
            Erstes Objekt anlegen
          </button>
        </Leer>
      ) : (
        <>
          <div className="legend">
            <span><span className="dot9" style={{ background: "var(--orange)" }} />
              In Sanierung: <b>{nachStatus.sanierung}</b></span>
            <span><span className="dot9" style={{ background: "var(--blue)" }} />
              Vermietbar: <b>{nachStatus.vermietbar}</b></span>
            <span><span className="dot9" style={{ background: "var(--green)" }} />
              Vermietet: <b>{nachStatus.vermietet}</b></span>
          </div>
          <div className="obj-list">
            {bestand.objekte.map((o) => <ObjektKarte key={o.id} objekt={o} />)}
          </div>
        </>
      )}

      {neuesObjekt ? <ObjektBlatt objekt={null} aufSchliessen={() => setzeNeuesObjekt(false)} /> : null}
    </>
  );
}

// ----------------------------------------------------------------- Objekte

export function ObjekteSeite() {
  const bestand = useBestand();
  const [neu, setzeNeu] = useState(false);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Objekte</h1>
          <p className="sub">{bestand.objekte.length} im Bestand</p>
        </div>
        <button className="btn btn-primary" onClick={() => setzeNeu(true)}>+ Objekt anlegen</button>
      </div>

      {bestand.objekte.length === 0
        ? <Leer titel="Noch keine Objekte.">
            <button className="btn btn-primary" style={{ marginTop: 14 }}
              onClick={() => setzeNeu(true)}>Objekt anlegen</button>
          </Leer>
        : <div className="obj-list">{bestand.objekte.map((o) => <ObjektKarte key={o.id} objekt={o} />)}</div>}

      {neu ? <ObjektBlatt objekt={null} aufSchliessen={() => setzeNeu(false)} /> : null}
    </>
  );
}

// ----------------------------------------------------------- Objekt-Detail

export function ObjektSeite({ objektId, reiter }: { objektId: string; reiter?: string }) {
  const bestand = useBestand();
  const { gehe, aendern } = useZustand();
  const objekt = bestand.objekte.find((o) => o.id === objektId);

  const [blatt, setzeBlatt] = useState<
    | { art: "objekt" }
    | { art: "einheit"; einheit: Einheit | null }
    | { art: "vertrag"; einheit: Einheit }
    | { art: "posten"; posten: Sanierungsposten | null }
    | { art: "mangel"; mangel: Mangel | null }
    | null>(null);

  if (!objekt) return <p className="sub">Objekt nicht gefunden.</p>;

  const aktiv = reiter ?? "einheiten";
  const k = objekt.kennzahlen;
  const ueberBudget = k.sanierungBudget > 0 && k.sanierungIst > k.sanierungBudget;
  const adresse = [objekt.strasse, [objekt.plz, objekt.ort].filter(Boolean).join(" ")]
    .filter(Boolean).join(", ");

  return (
    <>
      <button className="back" onClick={() => gehe({ seite: "objekte" })}>‹ Objekte</button>

      <div className="page-head">
        <div>
          <h1>{objekt.name}</h1>
          <p className="sub">{adresse || "—"} · Kauf: {datum(objekt.kaufdatum)}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-quiet btn-sm" onClick={() => setzeBlatt({ art: "objekt" })}>
            Stammdaten
          </button>
          <button className="btn btn-danger btn-sm" onClick={() => {
            if (!confirm(`„${objekt.name}“ mit allen Einheiten, Mietverträgen, Mängeln, `
              + "Inventar und Sanierungsposten unwiderruflich löschen?")) return;
            void aendern(() => api.objektLoeschen(objekt.id), "Objekt gelöscht")
              .then((ok) => { if (ok) gehe({ seite: "objekte" }); });
          }}>Löschen</button>
        </div>
      </div>

      <KennzahlKacheln k={k} zeigeInvestDetail />

      <div className="seg">
        {[
          ["einheiten", `Einheiten (${objekt.einheiten.length})`],
          ["maengel", `Mängel (${objekt.maengel.filter((m) => !m.erledigt).length})`],
          ["sanierung", `Sanierung (${objekt.sanierung.length})`],
          ["notizen", "Notizen"],
        ].map(([schluessel, text]) => (
          <button key={schluessel} className={aktiv === schluessel ? "on" : ""}
            onClick={() => gehe({ seite: "objekt", objektId, reiter: schluessel })}>{text}</button>
        ))}
      </div>

      {aktiv === "einheiten" ? (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button className="btn btn-primary btn-sm"
              onClick={() => setzeBlatt({ art: "einheit", einheit: null })}>+ Einheit</button>
          </div>
          {objekt.einheiten.length === 0
            ? <Leer titel="Noch keine Einheiten." />
            : objekt.einheiten.map((e) => (
              <EinheitZeile key={e.id} objekt={objekt} einheit={e}
                aufVertrag={(einheit) => setzeBlatt({ art: "vertrag", einheit })} />
            ))}
        </>
      ) : null}

      {aktiv === "maengel" ? (
        <MaengelListe
          eintraege={objekt.maengel.map((mangel) => ({ objekt, mangel }))}
          aufNeu={() => setzeBlatt({ art: "mangel", mangel: null })}
          aufBearbeiten={(_, mangel) => setzeBlatt({ art: "mangel", mangel })}
        />
      ) : null}

      {aktiv === "sanierung" ? (
        <SanierungsTabelle objekt={objekt} ueberBudget={ueberBudget}
          aufNeu={() => setzeBlatt({ art: "posten", posten: null })}
          aufBearbeiten={(posten) => setzeBlatt({ art: "posten", posten })} />
      ) : null}

      {aktiv === "notizen" ? (
        <div className="row-card" style={{
          display: "block", whiteSpace: "pre-wrap",
          color: objekt.notiz ? "var(--ink)" : "var(--muted)",
        }}>
          {objekt.notiz || "Keine Notizen. Über „Stammdaten“ hinzufügen."}
        </div>
      ) : null}

      {blatt?.art === "objekt"
        ? <ObjektBlatt objekt={objekt} aufSchliessen={() => setzeBlatt(null)} /> : null}
      {blatt?.art === "einheit"
        ? <EinheitBlatt objektId={objekt.id} einheit={blatt.einheit}
            aufSchliessen={() => setzeBlatt(null)} /> : null}
      {blatt?.art === "vertrag"
        ? <VertragBlatt einheit={blatt.einheit} aufSchliessen={() => setzeBlatt(null)} /> : null}
      {blatt?.art === "posten"
        ? <PostenBlatt objektId={objekt.id} posten={blatt.posten}
            aufSchliessen={() => setzeBlatt(null)} /> : null}
      {blatt?.art === "mangel"
        ? <MangelBlatt objekt={objekt} mangel={blatt.mangel}
            aufSchliessen={() => setzeBlatt(null)} /> : null}
    </>
  );
}

function SanierungsTabelle(
  { objekt, ueberBudget, aufNeu, aufBearbeiten }:
  {
    objekt: Objekt; ueberBudget: boolean;
    aufNeu: () => void; aufBearbeiten: (p: Sanierungsposten) => void;
  },
) {
  const k = objekt.kennzahlen;
  const rest = k.sanierungBudget - k.sanierungIst;

  return (
    <>
      <div className="san-head">
        <div>
          Budget: <b style={{ fontVariantNumeric: "tabular-nums" }}>{eur(k.sanierungBudget)}</b> · Ist:{" "}
          <b style={{ fontVariantNumeric: "tabular-nums", color: ueberBudget ? "var(--red)" : "var(--ink)" }}>
            {eur(k.sanierungIst)}
          </b>
          {k.sanierungBudget > 0 ? (
            <span className="sub" style={{ display: "inline", color: ueberBudget ? "var(--red)" : "var(--muted)" }}>
              {" "}({ueberBudget ? `+${eur(-rest)} über Budget` : `${eur(rest)} Rest`})
            </span>
          ) : null}
        </div>
        <button className="btn btn-primary btn-sm" onClick={aufNeu}>+ Posten</button>
      </div>

      {k.sanierungBudget > 0 ? (
        <div className="san-bar">
          <div className={ueberBudget ? "over" : ""}
            style={{ width: `${Math.min(100, (k.sanierungIst / k.sanierungBudget) * 100)}%` }} />
        </div>
      ) : null}

      {objekt.sanierung.length === 0 ? <Leer titel="Noch keine Sanierungsposten." /> : (
        <div className="table">
          <div className="trow head">
            <span>Gewerk / Posten</span><span className="num">Budget</span><span className="num">Ist</span>
            <span className="hide-m">Status</span><span className="hide-m" />
          </div>
          {objekt.sanierung.map((p) => {
            const drueber = p.budget > 0 && p.ist > p.budget;
            return (
              <div className="trow" key={p.id}>
                <div>
                  <div style={{ fontWeight: 600 }}>{p.gewerk}</div>
                  {p.faellig || p.notiz ? (
                    <div className="row-sub">
                      {p.faellig ? `Fällig: ${datum(p.faellig)}` : ""}
                      {p.faellig && p.notiz ? " · " : ""}{p.notiz}
                    </div>
                  ) : null}
                </div>
                <span className="num">{eur(p.budget)}</span>
                <span className={`num ${drueber ? "red" : ""}`}>{eur(p.ist)}</span>
                <span className={`gstat ${p.status} hide-m`}>{POSTEN_TEXT[p.status]}</span>
                <span className="hide-m">
                  <button className="btn btn-quiet btn-sm" onClick={() => aufBearbeiten(p)}>Bearbeiten</button>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------- Einheit-Detail

export function EinheitSeite(
  { objektId, einheitId, reiter }: { objektId: string; einheitId: string; reiter?: string },
) {
  const bestand = useBestand();
  const { gehe, aendern } = useZustand();
  const objekt = bestand.objekte.find((o) => o.id === objektId);
  const einheit = objekt?.einheiten.find((e) => e.id === einheitId);

  const [blatt, setzeBlatt] = useState<
    | { art: "einheit" }
    | { art: "vertrag" }
    | { art: "inventar"; raum?: string; position: InventarPosition | null }
    | { art: "mangel"; mangel: Mangel | null }
    | { art: "vorlage" }
    | null>(null);

  if (!objekt || !einheit) return <p className="sub">Einheit nicht gefunden.</p>;

  const aktiv = reiter ?? "inventar";
  const mv = einheit.mietvertrag;
  const eigeneMaengel = objekt.maengel.filter((m) => m.einheit_id === einheit.id);
  const offene = eigeneMaengel.filter((m) => !m.erledigt);
  const invKosten = einheit.inventar.reduce((s, i) => s + i.kosten, 0);
  const kritisch = einheit.inventar.filter(istKritisch).length;
  const vorlagenRaeume = [...new Set(bestand.vorlage.map((v) => v.raum))];
  const anzahlJeRaum = Object.fromEntries(
    vorlagenRaeume.map((r) => [r, bestand.vorlage.filter((v) => v.raum === r).length]));

  return (
    <>
      <button className="back" onClick={() => gehe({ seite: "objekt", objektId })}>
        ‹ {objekt.name}
      </button>

      <div className="page-head">
        <div>
          <h1>{einheit.bezeichnung}</h1>
          <p className="sub">
            {einheit.typ}{einheit.flaeche ? ` · ${einheit.flaeche} m²` : ""} ·{" "}
            <span className={`chip ${einheit.status}`}>{STATUS_TEXT[einheit.status]}</span>
            {einheit.moebliert ? <> <span className="chip moebel">möbliert</span></> : null}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-quiet btn-sm" onClick={() => setzeBlatt({ art: "einheit" })}>
            Stammdaten
          </button>
          <button className="btn btn-quiet btn-sm" onClick={() => setzeBlatt({ art: "vertrag" })}>
            {mv ? "Mietvertrag" : "+ Mietvertrag"}
          </button>
          <button className="btn btn-danger btn-sm" onClick={() => {
            if (!confirm(`Einheit „${einheit.bezeichnung}“ mit Inventar und Mängeln löschen?`)) return;
            void aendern(() => api.einheitLoeschen(einheit.id), "Einheit gelöscht")
              .then((ok) => { if (ok) gehe({ seite: "objekt", objektId }); });
          }}>Löschen</button>
        </div>
      </div>

      <div className="kpi-grid">
        <Kpi titel="Kaltmiete" ton="blue"
          wert={mv ? eur2(mv.kaltmiete) : einheit.zielmiete ? eur2(einheit.zielmiete) : "–"}
          unten={mv ? `Warm: ${eur2(Number(mv.kaltmiete) + Number(mv.nebenkosten_vz))}` : "Zielmiete"} />
        <Kpi titel="Offene Mängel" wert={String(offene.length)}
          unten={`${eigeneMaengel.length} insgesamt`} ton={offene.length > 0 ? "orange" : undefined} />
        <Kpi titel="Inventar-Positionen" wert={String(einheit.inventar.length)}
          unten={`${kritisch} fehlend / schlecht`} ton={kritisch > 0 ? "red" : undefined} />
        <Kpi titel="Neubeschaffung" wert={eur(invKosten)} unten="Summe „Neu: Kosten“" />
      </div>

      <div className="seg">
        {[["inventar", "Inventar / Bestandsaufnahme"], ["maengel", `Mängel (${offene.length})`]]
          .map(([schluessel, text]) => (
            <button key={schluessel} className={aktiv === schluessel ? "on" : ""}
              onClick={() => gehe({ seite: "einheit", objektId, einheitId, reiter: schluessel })}>
              {text}
            </button>
          ))}
      </div>

      {aktiv === "maengel" ? (
        <MaengelListe
          eintraege={eigeneMaengel.map((mangel) => ({ objekt, mangel }))}
          aufNeu={() => setzeBlatt({ art: "mangel", mangel: null })}
          aufBearbeiten={(_, mangel) => setzeBlatt({ art: "mangel", mangel })}
        />
      ) : (
        <>
          <div className="san-head">
            <div className="sub" style={{ margin: 0 }}>
              Bestandsaufnahme je Raum — Zustand, Fehlbestand und Neubeschaffungskosten
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="btn btn-quiet btn-sm" onClick={() => setzeBlatt({ art: "vorlage" })}>
                Vorlage einfügen
              </button>
              <button className="btn btn-primary btn-sm"
                onClick={() => setzeBlatt({ art: "inventar", position: null })}>+ Position</button>
            </div>
          </div>

          {einheit.inventar.length === 0 ? (
            <Leer titel="Noch keine Bestandsaufnahme.">
              Über „Vorlage einfügen“ die Standardliste laden
              ({bestand.vorlage.length} Positionen) und dann nur noch abhaken.
            </Leer>
          ) : (
            <>
              <InventarTabelle einheit={einheit}
                aufBearbeiten={(raum, position) => setzeBlatt({ art: "inventar", raum, position })} />
              <p className="sub" style={{ marginTop: 16 }}>
                Neubeschaffung gesamt: <b style={{ color: "var(--ink)" }}>{eur(invKosten)}</b> — diese
                Summe fließt nicht in die Gesamtinvestition, um Doppelzählung mit dem
                Sanierungsbudget zu vermeiden.
              </p>
            </>
          )}
        </>
      )}

      {blatt?.art === "einheit"
        ? <EinheitBlatt objektId={objekt.id} einheit={einheit}
            aufSchliessen={() => setzeBlatt(null)} /> : null}
      {blatt?.art === "vertrag"
        ? <VertragBlatt einheit={einheit} aufSchliessen={() => setzeBlatt(null)} /> : null}
      {blatt?.art === "inventar"
        ? <InventarBlatt einheitId={einheit.id} position={blatt.position} vorgabeRaum={blatt.raum}
            raumVorschlaege={raumVorschlaege(bestand.objekte, bestand.vorlage)}
            aufSchliessen={() => setzeBlatt(null)} /> : null}
      {blatt?.art === "mangel"
        ? <MangelBlatt objekt={objekt} mangel={blatt.mangel} vorgabeEinheitId={einheit.id}
            aufSchliessen={() => setzeBlatt(null)} /> : null}
      {blatt?.art === "vorlage"
        ? <VorlageAnwendenBlatt einheitId={einheit.id} raeume={vorlagenRaeume}
            anzahlJeRaum={anzahlJeRaum} aufSchliessen={() => setzeBlatt(null)} /> : null}
    </>
  );
}

// ------------------------------------------------------- Globale Übersichten

export function EinheitenSeite() {
  const bestand = useBestand();
  const [vertragFuer, setzeVertragFuer] = useState<Einheit | null>(null);
  const zeilen = alleEinheiten(bestand.objekte);

  return (
    <>
      <div className="page-head">
        <div><h1>Einheiten</h1><p className="sub">{zeilen.length} über alle Objekte</p></div>
      </div>
      {zeilen.length === 0
        ? <Leer titel="Keine Einheiten.">Einheiten werden im jeweiligen Objekt angelegt.</Leer>
        : zeilen.map(({ objekt, einheit }) => (
          <EinheitZeile key={einheit.id} objekt={objekt} einheit={einheit} zeigeObjekt
            aufVertrag={setzeVertragFuer} />
        ))}
      {vertragFuer
        ? <VertragBlatt einheit={vertragFuer} aufSchliessen={() => setzeVertragFuer(null)} /> : null}
    </>
  );
}

export function MaengelSeite() {
  const bestand = useBestand();
  const [blatt, setzeBlatt] = useState<{ objekt: Objekt; mangel: Mangel | null } | null>(null);
  const alle = alleMaengel(bestand.objekte);
  const offen = alle.filter(({ mangel }) => !mangel.erledigt);
  const hoch = offen.filter(({ mangel }) => mangel.prio === "hoch");
  const eigen = offen.filter(({ mangel }) => mangel.kostentraeger === "Eigentümer")
    .reduce((s, { mangel }) => s + mangel.kosten, 0);
  const dritte = offen.reduce((s, { mangel }) => s + mangel.kosten, 0) - eigen;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Mängelliste</h1>
          <p className="sub">{offen.length} offen · {alle.length - offen.length} erledigt</p>
        </div>
      </div>

      <div className="kpi-grid">
        <Kpi titel="Offen gesamt" wert={String(offen.length)} unten={`${alle.length} Einträge insgesamt`} />
        <Kpi titel="Priorität hoch" wert={String(hoch.length)} unten="vorrangig abarbeiten"
          ton={hoch.length > 0 ? "red" : undefined} />
        <Kpi titel="Kosten Eigentümer" wert={eur(eigen)} unten="offene Mängel" />
        <Kpi titel="Kosten Dritte" wert={eur(dritte)} unten="Mieter, Versicherung, Gewährleistung" />
      </div>

      {bestand.objekte.length === 0 ? (
        <Leer titel="Noch keine Objekte.">Mängel werden Objekten und Einheiten zugeordnet.</Leer>
      ) : (
        <MaengelListe eintraege={alle} zeigeObjekt
          aufNeu={bestand.objekte.length === 1
            ? () => setzeBlatt({ objekt: bestand.objekte[0]!, mangel: null })
            : undefined}
          aufBearbeiten={(objekt, mangel) => setzeBlatt({ objekt, mangel })} />
      )}

      {blatt ? <MangelBlatt objekt={blatt.objekt} mangel={blatt.mangel}
        aufSchliessen={() => setzeBlatt(null)} /> : null}
    </>
  );
}

export function InventarSeite() {
  const bestand = useBestand();
  const { gehe } = useZustand();
  const [anwenden, setzeAnwenden] = useState(false);
  const zeilen = alleEinheiten(bestand.objekte)
    .filter(({ einheit }) => einheit.inventar.length > 0 || einheit.moebliert);
  const vorlagenRaeume = [...new Set(bestand.vorlage.map((v) => v.raum))];
  const anzahlJeRaum = Object.fromEntries(
    vorlagenRaeume.map((r) => [r, bestand.vorlage.filter((v) => v.raum === r).length]));

  return (
    <>
      <div className="page-head">
        <div><h1>Inventar</h1><p className="sub">Bestandsaufnahmen möblierter Einheiten</p></div>
      </div>

      <div className="san-head">
        <div className="sub" style={{ margin: 0 }}>Vorlage: {bestand.vorlage.length} Positionen</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn btn-quiet btn-sm" onClick={() => gehe({ seite: "vorlage" })}>
            Vorlage bearbeiten
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setzeAnwenden(true)}>
            Auf alle Einheiten anwenden
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <Kpi titel="Einheiten mit Inventar" wert={String(zeilen.length)} />
        <Kpi titel="Kritische Positionen" wert={String(bestand.kennzahlen.inventarKritisch)}
          unten="fehlend oder Zustand schlecht"
          ton={bestand.kennzahlen.inventarKritisch > 0 ? "red" : undefined} />
        <Kpi titel="Neubeschaffung gesamt" wert={eur(bestand.kennzahlen.inventarNeubeschaffung)}
          unten="nicht im Invest enthalten" />
      </div>

      {zeilen.length === 0 ? (
        <Leer titel="Keine Bestandsaufnahmen.">
          Markiere eine Einheit als „möbliert“ und lege im Einheiten-Detail eine Raumliste an.
        </Leer>
      ) : zeilen.map(({ objekt, einheit }) => {
        const kosten = einheit.inventar.reduce((s, i) => s + i.kosten, 0);
        const kritisch = einheit.inventar.filter(istKritisch).length;
        const raeume = new Set(einheit.inventar.map((i) => i.raum || "Allgemein")).size;
        return (
          <div className="row-card" key={einheit.id}>
            <div style={{ minWidth: 200 }}>
              <div className="row-title">
                <button className="linkish" style={{ fontSize: 15, fontWeight: 600 }}
                  onClick={() => gehe({ seite: "einheit", objektId: objekt.id, einheitId: einheit.id })}>
                  {einheit.bezeichnung}
                </button>
                {einheit.moebliert ? <span className="chip moebel">möbliert</span> : null}
              </div>
              <div className="row-sub">
                <button className="linkish" onClick={() => gehe({ seite: "objekt", objektId: objekt.id })}>
                  {objekt.name}
                </button>
                {" · "}{einheit.inventar.length} Positionen{raeume ? ` in ${raeume} Räumen` : ""}
              </div>
            </div>
            <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
              <Mini titel="Kritisch" wert={String(kritisch)} ton={kritisch > 0 ? "red" : undefined} />
              <Mini titel="Neu: Kosten" wert={eur(kosten)} />
              <button className="btn btn-quiet btn-sm"
                onClick={() => gehe({ seite: "einheit", objektId: objekt.id, einheitId: einheit.id })}>
                Öffnen
              </button>
            </div>
          </div>
        );
      })}

      {anwenden ? <VorlageAnwendenBlatt einheitId={null} raeume={vorlagenRaeume}
        anzahlJeRaum={anzahlJeRaum} aufSchliessen={() => setzeAnwenden(false)} /> : null}
    </>
  );
}

export function VorlageSeite() {
  const bestand = useBestand();
  const [blatt, setzeBlatt] = useState<{ position: VorlagePosition | null } | null>(null);
  const [anwenden, setzeAnwenden] = useState(false);
  const [ausEinheit, setzeAusEinheit] = useState(false);

  const raeume = [...new Set(bestand.vorlage.map((v) => v.raum))];
  const anzahlJeRaum = Object.fromEntries(
    raeume.map((r) => [r, bestand.vorlage.filter((v) => v.raum === r).length]));
  const einheiten = alleEinheiten(bestand.objekte);
  const fehlend = einheiten.reduce((summe, { einheit }) =>
    summe + bestand.vorlage.filter((v) => !einheit.inventar.some((i) =>
      i.raum.toLowerCase() === v.raum.toLowerCase()
      && i.bezeichnung.toLowerCase() === v.bezeichnung.toLowerCase())).length, 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Inventar-Vorlage</h1>
          <p className="sub">
            Standardliste für neue Einheiten — {bestand.vorlage.length} Positionen in {raeume.length} Räumen
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setzeBlatt({ position: null })}>
          + Position
        </button>
      </div>

      <div className="kpi-grid">
        <Kpi titel="Positionen" wert={String(bestand.vorlage.length)} unten={`${raeume.length} Räume`} />
        <Kpi titel="Einheiten" wert={String(einheiten.length)} unten="im Portfolio" />
        <Kpi titel="Noch nicht angelegt" wert={String(fehlend)}
          unten="Vorlagenpositionen über alle Einheiten" ton={fehlend > 0 ? "orange" : undefined} />
      </div>

      <div className="san-head">
        <div className="sub" style={{ margin: 0 }}>
          Wird die Vorlage angewendet, entsteht jede Position mit Bestand „noch offen“ —
          du hakst nur ab.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn btn-quiet btn-sm" onClick={() => setzeAusEinheit(true)}>
            Aus Einheit übernehmen
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setzeAnwenden(true)}>
            Auf alle Einheiten anwenden
          </button>
        </div>
      </div>

      {bestand.vorlage.length === 0 ? (
        <Leer titel="Vorlage ist leer.">
          Positionen anlegen oder eine bestehende Einheit als Vorlage übernehmen.
        </Leer>
      ) : raeume.map((raum) => (
        <div className="inv-room" key={raum}>
          <div className="inv-room-hd">
            <h3>{raum}</h3>
            <span className="sub" style={{ margin: 0 }}>{anzahlJeRaum[raum]} Positionen</span>
          </div>
          <div className="inv">
            {bestand.vorlage.filter((v) => v.raum === raum).map((p) => (
              <div className="irow" key={p.id} style={{ gridTemplateColumns: "1fr auto" }}>
                <div>
                  <b style={{ fontWeight: 600 }}>{p.bezeichnung}</b>
                  {p.pflicht === "optional" ? <span className="opt-tag">optional</span> : null}
                </div>
                <span style={{ display: "flex", gap: 6 }}>
                  <button className="btn-xs" onClick={() => setzeBlatt({ position: p })}>Bearb.</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}

      <p className="sub" style={{ marginTop: 16 }}>
        Änderungen an der Vorlage wirken nur auf künftige Anwendungen — bereits
        erfasste Inventare bleiben unberührt.
      </p>

      {blatt ? <VorlageBlatt position={blatt.position}
        raumVorschlaege={raumVorschlaege(bestand.objekte, bestand.vorlage)}
        aufSchliessen={() => setzeBlatt(null)} /> : null}
      {anwenden ? <VorlageAnwendenBlatt einheitId={null} raeume={raeume}
        anzahlJeRaum={anzahlJeRaum} aufSchliessen={() => setzeAnwenden(false)} /> : null}
      {ausEinheit ? <VorlageAusEinheitBlatt einheiten={einheiten}
        aufSchliessen={() => setzeAusEinheit(false)} /> : null}
    </>
  );
}

export function SanierungSeite() {
  const bestand = useBestand();
  const { gehe } = useZustand();
  const mitPosten = bestand.objekte.filter((o) => o.sanierung.length > 0);
  const k = bestand.kennzahlen;
  const offen = bestand.objekte.flatMap((o) => o.sanierung).filter((p) => p.status !== "fertig").length;
  const ueberBudget = k.sanierungBudget > 0 && k.sanierungIst > k.sanierungBudget;

  return (
    <>
      <div className="page-head">
        <div><h1>Sanierung</h1><p className="sub">{offen} offene Posten über alle Objekte</p></div>
      </div>

      <div className="kpi-grid">
        <Kpi titel="Budget gesamt" wert={eur(k.sanierungBudget)} />
        <Kpi titel="Ist gesamt" wert={eur(k.sanierungIst)} ton={ueberBudget ? "red" : undefined}
          unten={ueberBudget ? "über Budget" : `Rest: ${eur(k.sanierungBudget - k.sanierungIst)}`} />
        <Kpi titel="Offene Posten" wert={String(offen)} unten="Status „Offen“ oder „Läuft“" />
      </div>

      {mitPosten.length === 0
        ? <Leer titel="Keine Sanierungsposten.">Posten werden im Objekt angelegt.</Leer>
        : mitPosten.map((o) => (
          <div key={o.id}>
            <div style={{
              marginTop: 24, marginBottom: 10, display: "flex", justifyContent: "space-between",
              alignItems: "baseline", flexWrap: "wrap", gap: 8,
            }}>
              <button className="linkish big" onClick={() => gehe({ seite: "objekt", objektId: o.id, reiter: "sanierung" })}>
                {o.name}
              </button>
              <span className="sub">
                Budget {eur(o.kennzahlen.sanierungBudget)} · Ist{" "}
                <b>{eur(o.kennzahlen.sanierungIst)}</b>
              </span>
            </div>
            <div className="table">
              {o.sanierung.map((p) => (
                <div className="trow" key={p.id}>
                  <div><div style={{ fontWeight: 600 }}>{p.gewerk}</div></div>
                  <span className="num">{eur(p.budget)}</span>
                  <span className="num">{eur(p.ist)}</span>
                  <span className={`gstat ${p.status} hide-m`}>{POSTEN_TEXT[p.status]}</span>
                  <span className="hide-m" />
                </div>
              ))}
            </div>
          </div>
        ))}
    </>
  );
}

export function MieterSeite() {
  const bestand = useBestand();
  const { gehe } = useZustand();
  const [vertragFuer, setzeVertragFuer] = useState<Einheit | null>(null);
  const zeilen = alleEinheiten(bestand.objekte).filter(({ einheit }) => einheit.mietvertrag);

  return (
    <>
      <div className="page-head">
        <div><h1>Mieter</h1><p className="sub">{zeilen.length} aktiv</p></div>
      </div>
      {zeilen.length === 0
        ? <Leer titel="Keine Mieter.">Mietverträge werden an der Einheit angelegt.</Leer>
        : zeilen.map(({ objekt, einheit }) => {
          const mv = einheit.mietvertrag!;
          return (
            <div className="row-card" key={einheit.id}>
              <div style={{ minWidth: 190 }}>
                <div className="row-title">{mv.mieter_name}</div>
                <div className="row-sub">
                  <button className="linkish" onClick={() => gehe({ seite: "objekt", objektId: objekt.id })}>
                    {objekt.name}
                  </button>
                  {" · "}
                  <button className="linkish"
                    onClick={() => gehe({ seite: "einheit", objektId: objekt.id, einheitId: einheit.id })}>
                    {einheit.bezeichnung}
                  </button>
                  {mv.kontakt ? ` · ${mv.kontakt}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
                <Mini titel="Kaltmiete" wert={eur2(mv.kaltmiete)} ton="blue" />
                <Mini titel="seit" wert={datum(mv.beginn)} />
                <button className="btn btn-quiet btn-sm" onClick={() => setzeVertragFuer(einheit)}>
                  Vertrag
                </button>
              </div>
            </div>
          );
        })}
      {vertragFuer
        ? <VertragBlatt einheit={vertragFuer} aufSchliessen={() => setzeVertragFuer(null)} /> : null}
    </>
  );
}

export function VertraegeSeite() {
  const bestand = useBestand();
  const { gehe } = useZustand();
  const [vertragFuer, setzeVertragFuer] = useState<Einheit | null>(null);
  const zeilen = alleEinheiten(bestand.objekte).filter(({ einheit }) => einheit.mietvertrag);
  const summe = zeilen.reduce((s, { einheit }) => s + Number(einheit.mietvertrag!.kaltmiete), 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Mietverträge</h1>
          <p className="sub">{zeilen.length} aktiv · Kaltmiete gesamt: {eur2(summe)} / Monat</p>
        </div>
      </div>
      {zeilen.length === 0 ? <Leer titel="Keine Mietverträge." /> : zeilen.map(({ objekt, einheit }) => {
        const mv = einheit.mietvertrag!;
        return (
          <div className="row-card" key={einheit.id}>
            <div style={{ minWidth: 190 }}>
              <div className="row-title">
                <button className="linkish" style={{ fontSize: 15, fontWeight: 600 }}
                  onClick={() => gehe({ seite: "einheit", objektId: objekt.id, einheitId: einheit.id })}>
                  {einheit.bezeichnung}
                </button>
                {einheit.moebliert ? <span className="chip moebel">möbliert</span> : null}
              </div>
              <div className="row-sub">
                <button className="linkish" onClick={() => gehe({ seite: "objekt", objektId: objekt.id })}>
                  {objekt.name}
                </button>
                {" · Mieter: "}{mv.mieter_name}
                {einheit.fruehereVertraege.length > 0
                  ? ` · ${einheit.fruehereVertraege.length} frühere`
                  : ""}
              </div>
            </div>
            <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
              <Mini titel="Kalt" wert={eur2(mv.kaltmiete)} ton="blue" />
              <Mini titel="Warm" wert={eur2(Number(mv.kaltmiete) + Number(mv.nebenkosten_vz))} />
              <Mini titel="Kaution" wert={eur(mv.kaution)} />
              <Mini titel="Beginn" wert={datum(mv.beginn)} />
              <button className="btn btn-quiet btn-sm" onClick={() => setzeVertragFuer(einheit)}>
                Bearbeiten
              </button>
            </div>
          </div>
        );
      })}
      {vertragFuer
        ? <VertragBlatt einheit={vertragFuer} aufSchliessen={() => setzeVertragFuer(null)} /> : null}
    </>
  );
}

// ------------------------------------------------------------------- Konto

export function KontoSeite() {
  const bestand = useBestand();
  const { benutzer, abmelden, zeigeMeldung } = useZustand();
  const einheiten = alleEinheiten(bestand.objekte).length;
  const maengel = bestand.objekte.flatMap((o) => o.maengel).length;
  const inventar = bestand.objekte.flatMap((o) => o.einheiten.flatMap((e) => e.inventar)).length;
  const anhaenge = bestand.objekte.flatMap((o) => [
    ...o.dateien,
    ...o.maengel.flatMap((m) => m.dateien),
    ...o.einheiten.flatMap((e) => e.dateien),
  ]).length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Konto & Daten</h1>
          <p className="sub">Angemeldet als {benutzer?.name} ({benutzer?.email})</p>
        </div>
      </div>

      <div className="more-sec">Datenbestand</div>
      <div className="kpi-grid">
        <Kpi titel="Objekte" wert={String(bestand.objekte.length)} />
        <Kpi titel="Einheiten" wert={String(einheiten)} />
        <Kpi titel="Mängel" wert={String(maengel)} unten={`${anhaenge} Anhänge`} />
        <Kpi titel="Inventarpositionen" wert={String(inventar)} />
      </div>

      <BenutzerVerwaltung />

      <div className="more-sec">Sitzung</div>
      <div className="more-list">
        <button className="more-item" onClick={() => void abmelden()}>
          <span className="ico">🚪</span><span>Abmelden</span><span className="more-arrow">›</span>
        </button>
        <button className="more-item" onClick={async () => {
          if (!confirm("Alle Sitzungen beenden? Auch alle anderen Geräte werden abgemeldet.")) return;
          const { beendet } = await api.ueberallAbmelden();
          zeigeMeldung(`${beendet} Sitzungen beendet.`);
          await abmelden();
        }}>
          <span className="ico">🔒</span>
          <span>Auf allen Geräten abmelden</span>
          <span className="more-arrow">›</span>
        </button>
      </div>
      <p className="sub" style={{ marginTop: 8 }}>
        „Auf allen Geräten abmelden“ ist der Weg, wenn ein Telefon verloren geht.
        Ein vergessenes Passwort setzt jemand anders über die Kontenliste oben
        zurück — es gibt bewusst keine Passwort-vergessen-Mail.
      </p>

      <div className="more-sec">Export</div>
      <div className="more-list">
        <a className="more-item" href="/api/export/json?fotos=ja" download>
          <span className="ico">⬇️</span>
          <span>Vollständige Sicherung (JSON inklusive Fotos)</span>
          <span className="more-arrow">›</span>
        </a>
        <a className="more-item" href="/api/export/json?fotos=nein" download>
          <span className="ico">📄</span>
          <span>Sicherung ohne Fotos (kleiner)</span>
          <span className="more-arrow">›</span>
        </a>
        <a className="more-item" href="/api/export/maengel.csv" download>
          <span className="ico">📊</span>
          <span>Mängelliste als CSV</span>
          <span className="more-arrow">›</span>
        </a>
        <a className="more-item" href="/api/export/inventar.csv" download>
          <span className="ico">📦</span>
          <span>Inventar als CSV</span>
          <span className="more-arrow">›</span>
        </a>
      </div>
      <p className="sub" style={{ marginTop: 8 }}>
        Das JSON hat die Struktur der alten Einzeldatei-App und lässt sich dort
        wieder öffnen — der Notausgang, falls der Server einmal nicht erreichbar
        ist. Nicht enthalten sind Vertragshistorie, Anhänge außerhalb von Mängeln
        und das Feld „investiv“; der Export benennt das selbst.
        Die CSV-Dateien öffnen sich in Excel mit Semikolon und Dezimalkomma.
      </p>

      <div className="more-sec">Sicherung durch den Server</div>
      <p className="sub">
        Datenbank und Dateiablage werden nächtlich gesichert
        (<code>infra/sicherung.sh</code>). Eine Sicherung, die noch nie
        zurückgespielt wurde, ist eine Vermutung — der Rückweg steht im Skript
        und sollte einmal geübt werden.
      </p>
    </>
  );
}

// -------------------------------------------------------------- Platzhalter

const BALD: Record<string, { symbol: string; titel: string; text: string }> = {
  eigentuemer: { symbol: "👤", titel: "Eigentümer", text: "Eigentümerstruktur je Objekt — GbR-Anteile, privat oder GmbH — mit Zuordnung zu den Kennzahlen." },
  bank: { symbol: "🏦", titel: "Bankkonten", text: "Einlesen von Umsätzen als CSV oder CAMT.053 und Zuordnung zu Mietverträgen und Buchungen." },
  eua: { symbol: "💰", titel: "Einnahmen & Ausgaben", text: "Das Rückgrat der Finanzmodule: Buchungen je Objekt und Einheit, mit Kennzeichen umlagefähig." },
  nk: { symbol: "🧾", titel: "Nebenkosten", text: "Abrechnungszeitraum, Umlageschlüssel, Gegenrechnung der Vorauszahlungen — als Rechenhilfe für die Steuerberatung." },
  dokumente: { symbol: "📁", titel: "Dokumente", text: "Zentrale Ablage für Kaufverträge, Mietverträge, Angebote und Rechnungen. Die Dateiablage dahinter steht bereits." },
  kommunikation: { symbol: "💬", titel: "Kommunikation", text: "Nachrichtenverlauf mit Mietern und Handwerkern, dokumentiert je Einheit und Mangel." },
  aufgaben: { symbol: "✅", titel: "Aufgaben", text: "Allgemeine Aufgaben mit Fälligkeiten — Zählerstände, Wartungen, Fristen. Mängel sind bereits abgedeckt." },
};

export function BaldSeite({ thema }: { thema: string }) {
  const s = BALD[thema] ?? { symbol: "🚧", titel: "In Planung", text: "" };
  return (
    <div className="soon-page">
      <div className="big">{s.symbol}</div>
      <h2>{s.titel}</h2>
      <p>{s.text}</p>
      <span className="soon-badge">IN ARBEIT</span>
    </div>
  );
}
